import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import Dropzone from 'react-dropzone';
import { Button } from '@this/shared/ui/inputs/button';
import { Loading } from '@this/components/shared/ui/feedbacks/loading/loading';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@this/src/components/shared/ui/feedbacks/modal';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@this/components/shared/ui/data_displays/table';
import { Alert } from '@this/components/shared/ui/feedbacks/alert/alert';
import { Heading } from '@this/components/shared/ui/data_displays/typography/heading/heading';
import { Text } from '@this/components/shared/ui/data_displays/typography/text/text';
import { Fetcher, HTTPError } from '@this/src/util';
import { formatPrice } from '@this/src/util/price/price.util';
import TaskalCallFee, {
  convertTaskalCallFeeResponseToArgs,
  type TaskalCallFeeResponseArgs,
  type TaskalCallFeeUpdateSubmitArgs
} from '@this/domain/taskal_call_fee';
import InlineEditNumberCell from './inline_edit_number_cell';

type IndexResponse = {
  fees: TaskalCallFeeResponseArgs[];
};

type UpdateResponse = {
  fee: TaskalCallFeeResponseArgs;
};

type UploadResponse = {
  id: number;
};

type ImportActionStatus = 'enqueued' | 'running' | 'success' | 'fail';

type ImportActionShowResponse = {
  action: {
    id: number;
    status: ImportActionStatus;
    filename: string | null;
    success_count: number;
    fail_count: number;
    skip_count: number;
    total_count: number;
    error_messages: string[];
  };
};

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60000;

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

const currentYear = () => new Date().getFullYear();
const currentMonth = () => new Date().getMonth() + 1;

const wrapperStyle: React.CSSProperties = {
  padding: '20px',
  fontSize: '12px',
  display: 'flex',
  flexDirection: 'column',
  height: '100%'
};

const headerAreaStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '10px'
};

const monthSelectorStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '10px'
};

const summaryAreaStyle: React.CSSProperties = {
  display: 'flex',
  gap: '20px',
  marginBottom: '10px',
  padding: '10px',
  background: '#f5f5f5',
  borderRadius: '4px'
};

const tableWrapperStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'auto'
};

const dropzoneStyle: React.CSSProperties = {
  width: '100%',
  height: '100px',
  background: '#eee',
  border: '2px dotted #999',
  textAlign: 'center',
  padding: '20px'
};

const alertMarginStyle: React.CSSProperties = { marginBottom: '10px' };

const TaskalCallFees = observer(() => {
  const [year, setYear] = useState<number>(currentYear());
  const [month, setMonth] = useState<number>(currentMonth());
  const [fees, setFees] = useState<TaskalCallFee[]>([]);
  const [fetching, setFetching] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadInfo, setUploadInfo] = useState<string | null>(null);
  const [uploadWarning, setUploadWarning] = useState<string | null>(null);
  const [importing, setImporting] = useState<boolean>(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const totalCount = fees.length;
  const totalAmount = useMemo(() => fees.reduce((sum, f) => sum + (f.amount ?? 0), 0), [fees]);

  const fetchFees = useCallback(async () => {
    setFetching(true);
    setFetchError(null);
    try {
      const res = await Fetcher.get<IndexResponse>('/arrangement/taskal_call_fees.json', { year, month });
      setFees(res.fees.map(r => new TaskalCallFee(convertTaskalCallFeeResponseToArgs(r))));
    } catch {
      setFetchError('データの取得に失敗しました。');
    } finally {
      setFetching(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchFees();
  }, [fetchFees]);

  useEffect(() => {
    document.title = 'TASKAL入電費用';
    return () => {
      document.title = '管理画面';
    };
  }, []);

  const pollImportAction = async (actionId: number) => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await sleep(POLL_INTERVAL_MS);
      try {
        const res = await Fetcher.get<ImportActionShowResponse>(
          `/arrangement/taskal_call_fee_excel_import_actions/${actionId}.json`
        );
        const { status, success_count, fail_count, skip_count, error_messages } = res.action;
        const skipSuffix = skip_count > 0 ? ` / スキップ: ${skip_count}行` : '';
        if (status === 'success') {
          if (skip_count > 0) {
            setUploadInfo(null);
            setUploadWarning(
              `取り込みが完了しました。（成功: ${success_count}件${skipSuffix}）日付が入力されていない行は取り込まれません。`
            );
          } else {
            setUploadInfo(`取り込みが完了しました。（成功: ${success_count}件）`);
          }
          fetchFees();
          return;
        }
        if (status === 'fail') {
          const detail = error_messages[0] ?? '取り込み中にエラーが発生しました。';
          setUploadError(
            `取り込みに失敗しました（成功: ${success_count}件 / 失敗: ${fail_count}件${skipSuffix}）: ${detail}`
          );
          fetchFees();
          return;
        }
      } catch {
        // 単発エラーは継続してリトライ（タイムアウトで打ち切られる）
      }
    }
    setUploadInfo(
      '取り込み状況の確認がタイムアウトしました。時間をおいて月送りボタンまたは再読み込みで一覧を更新してください。'
    );
  };

  const handleUpload = async () => {
    if (!file) return;
    const data = new FormData();
    data.append('file', file);
    setUploading(true);
    setUploadError(null);
    setUploadWarning(null);
    let actionId: number | null = null;
    try {
      const res = await Fetcher.upload<UploadResponse>('/arrangement/taskal_call_fee_excel_import_actions', data);
      actionId = res.id;
      setFile(null);
      setShowUploadModal(false);
      setUploadInfo('取り込みを開始しました。バックグラウンド処理の完了を待っています…');
    } catch (e) {
      const message = e instanceof HTTPError ? (e.response?.data?.error as string | undefined) : undefined;
      setUploadError(message ?? '取り込みに失敗しました。ファイル形式を確認してください。');
    } finally {
      setUploading(false);
    }
    if (actionId != null) {
      setImporting(true);
      try {
        await pollImportAction(actionId);
      } finally {
        setImporting(false);
      }
    }
  };

  const submitUpdate = async (feeId: number, params: TaskalCallFeeUpdateSubmitArgs): Promise<boolean> => {
    setUpdatingId(feeId);
    setFetchError(null);
    try {
      const res = await Fetcher.put<UpdateResponse>(`/arrangement/taskal_call_fees/${feeId}`, params);
      const updated = new TaskalCallFee(convertTaskalCallFeeResponseToArgs(res.fee));
      setFees(prev => prev.map(f => (f.id === updated.id ? updated : f)));
      return true;
    } catch (e) {
      const errors = e instanceof HTTPError ? (e.response?.data?.errors as string[] | undefined) : undefined;
      setFetchError(errors?.[0] ?? '更新に失敗しました。');
      return false;
    } finally {
      setUpdatingId(null);
    }
  };

  const handleMonthChange = (delta: number) => {
    let newMonth = month + delta;
    let newYear = year;
    if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    }
    setYear(newYear);
    setMonth(newMonth);
  };

  return (
    <div style={wrapperStyle}>
      <div style={headerAreaStyle}>
        <Heading level="3">TASKAL入電費用</Heading>
        <Button onClick={() => setShowUploadModal(true)} disabled={importing}>
          取り込み
        </Button>
      </div>

      <div style={monthSelectorStyle}>
        <Button color="sub" size="small" onClick={() => handleMonthChange(-1)}>
          ◀
        </Button>
        <Text component="span" bold>
          {year}年{month}月
        </Text>
        <Button color="sub" size="small" onClick={() => handleMonthChange(1)}>
          ▶
        </Button>
      </div>

      <div style={summaryAreaStyle}>
        <Text component="span">
          件数合計:{' '}
          <Text component="span" bold>
            {totalCount}件
          </Text>
        </Text>
        <Text component="span">
          金額合計:{' '}
          <Text component="span" bold>
            {formatPrice(totalAmount)}
          </Text>
        </Text>
      </div>

      {uploadInfo && (
        <Alert
          variant={importing ? 'info' : 'success'}
          onClose={importing ? undefined : () => setUploadInfo(null)}
          style={alertMarginStyle}
        >
          {uploadInfo}
        </Alert>
      )}
      {uploadWarning && (
        <Alert variant="warning" onClose={() => setUploadWarning(null)} style={alertMarginStyle}>
          {uploadWarning}
        </Alert>
      )}
      {fetchError && (
        <Alert variant="danger" onClose={() => setFetchError(null)} style={alertMarginStyle}>
          {fetchError}
        </Alert>
      )}

      {fetching ? (
        <Loading />
      ) : (
        <div style={tableWrapperStyle}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell nowrap>日付</TableCell>
                <TableCell nowrap>曜日</TableCell>
                <TableCell nowrap>時間</TableCell>
                <TableCell nowrap>予約番号</TableCell>
                <TableCell nowrap>相手先会社名</TableCell>
                <TableCell nowrap>通話相手名前</TableCell>
                <TableCell nowrap>対応者</TableCell>
                <TableCell nowrap>入電/交信回数</TableCell>
                <TableCell nowrap>料金</TableCell>
                <TableCell nowrap>旅程ID</TableCell>
                <TableCell nowrap>組織名</TableCell>
                <TableCell nowrap>登録日時</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fees.map(fee => (
                <TableRow key={fee.id}>
                  <TableCell nowrap>{fee.callDate}</TableCell>
                  <TableCell nowrap>{fee.dayOfWeek}</TableCell>
                  <TableCell nowrap>{fee.callTimeJst}</TableCell>
                  <TableCell nowrap>{fee.reservationNumber}</TableCell>
                  <TableCell nowrap>{fee.counterpartCompanyName}</TableCell>
                  <TableCell nowrap>{fee.callRecipientName}</TableCell>
                  <TableCell nowrap>{fee.personInCharge}</TableCell>
                  <TableCell nowrap>{fee.incomingCallCount}</TableCell>
                  <TableCell nowrap>
                    <InlineEditNumberCell
                      value={fee.amount}
                      allowNull={false}
                      disabled={updatingId === fee.id}
                      onSubmit={next => submitUpdate(fee.id, { amount: next as number })}
                    />
                  </TableCell>
                  <TableCell nowrap>
                    <InlineEditNumberCell
                      value={fee.tripId}
                      allowNull
                      placeholder="旅程ID"
                      disabled={updatingId === fee.id}
                      onSubmit={next => submitUpdate(fee.id, { trip_id: next })}
                    />
                  </TableCell>
                  <TableCell nowrap>{fee.organizationName}</TableCell>
                  <TableCell nowrap>{fee.createdAt}</TableCell>
                </TableRow>
              ))}
              {fees.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    データがありません
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Modal open={showUploadModal} onClose={() => setShowUploadModal(false)}>
        <ModalHeader>エクセルファイル取り込み</ModalHeader>
        <ModalBody>
          <Dropzone onDrop={(files: File[]) => setFile(files[0])} accept=".xlsx,.xls" style={dropzoneStyle}>
            <Text>ここへエクセルファイルをドラッグ&amp;ドロップ、またはクリックしてファイルを指定</Text>
          </Dropzone>
          {file && (
            <Text component="div" style={{ marginTop: '10px' }}>
              {file.name}
            </Text>
          )}
          {uploadError && (
            <Alert variant="danger" onClose={() => setUploadError(null)} style={{ marginTop: '10px' }}>
              {uploadError}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="sub" onClick={() => setShowUploadModal(false)} style={{ marginRight: '10px' }}>
            キャンセル
          </Button>
          {uploading ? (
            <div style={{ width: '100px' }}>
              <Loading />
            </div>
          ) : (
            <Button onClick={handleUpload} disabled={!file}>
              アップロード
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
});

export default TaskalCallFees;
